import { z } from "zod";
import { zId, zInstanteUtc } from "./dominio.js";
/**
 * Passkey (WebAuthn) — RN-36.
 *
 * Duas regras mandam em tudo aqui, e as duas são do produto, não da biblioteca:
 *
 *  1. **A senha continua sendo um caminho.** A passkey soma; nunca substitui.
 *     Um app sem recuperação de senha por e-mail (decisão da F0) não pode ter
 *     como única entrada uma chave que vive dentro de um aparelho.
 *  2. **Mais de um aparelho.** Celular e PC, cada um com a sua credencial. Se
 *     entrar dependesse de um aparelho só, perder o telefone trancaria a conta.
 *
 * As opções da cerimônia viajam como objeto opaco: quem define a forma delas é
 * o WebAuthn, e reescrever esses schemas aqui seria manter à mão uma cópia de
 * uma especificação que não é nossa. O que o contrato guarda é o que **é**
 * nosso: o par desafio/resposta e a lista de aparelhos.
 */
/** Opções de cerimônia, como a especificação do WebAuthn as define. */
export const zOpcoesDaCerimonia = z.object({
    /**
     * O desafio não viaja de volta sozinho: viaja este id, e o desafio fica no
     * banco. Assim o servidor não precisa confiar em nada que o cliente devolve
     * para saber o que ele mesmo pediu.
     */
    desafioId: zId,
    opcoes: z.record(z.string(), z.unknown()),
});
export const zRegistroDePasskey = z.object({
    desafioId: zId,
    /** "iPhone", "PC de casa". É por ele que se reconhece o que remover depois. */
    apelido: z.string().trim().min(1, "Dê um nome ao aparelho").max(60),
    resposta: z.record(z.string(), z.unknown()),
});
export const zEntradaComPasskey = z.object({
    desafioId: zId,
    resposta: z.record(z.string(), z.unknown()),
});
export const zCredencialNaLista = z.object({
    id: zId,
    apelido: z.string(),
    /** `multiDevice` é chave sincronizada pelo chaveiro; `singleDevice` mora num aparelho só. */
    tipoDispositivo: z.string(),
    copiada: z.boolean(),
    transportes: z.array(z.string()),
    ultimoUso: zInstanteUtc.nullable(),
    criadoEm: zInstanteUtc,
});
export const zListaDeCredenciais = z.object({ credenciais: z.array(zCredencialNaLista) });
export const zCredencialId = z.object({ id: zId });
/**
 * Um desafio serve **uma** cerimônia, do tipo certo, do dono certo, dentro da
 * validade. A conta é pura para ser testável sem banco e sem relógio.
 *
 * O uso único não está aqui: ele vem de apagar a linha ao consumir. Esta função
 * decide se o que foi encontrado ainda vale — quem não encontra nada já não
 * chega a perguntar.
 */
export function conferirDesafio(guardado, esperado, agora) {
    if (guardado.proposito !== esperado.proposito)
        return "proposito";
    // Um desafio de registro é do usuário que o pediu. Aceitar o de outro seria
    // deixar uma sessão cadastrar passkey na conta alheia.
    if (esperado.usuarioId !== undefined && guardado.usuarioId !== esperado.usuarioId)
        return "usuario";
    if (guardado.expiraEm <= agora)
        return "vencido";
    return "ok";
}
// ---------------------------------------------------------------- ambiente
/**
 * O `rpID` é o domínio para o qual a passkey nasce, e o navegador recusa a
 * cerimônia se ele não cobrir a origem da página. Configurar os dois em
 * desacordo faz **toda** entrada falhar, no aparelho, com uma mensagem que não
 * ajuda ninguém — por isso a conferência acontece na subida do processo, e não
 * na primeira tentativa de login.
 *
 * A regra é a da especificação: o host da origem precisa ser o `rpID` ou um
 * subdomínio dele. `https` é exigido fora de `localhost`, que é a exceção que o
 * próprio WebAuthn abre para desenvolvimento.
 */
export function origemCombinaComRpId(origem, rpId) {
    let url;
    try {
        url = new URL(origem);
    }
    catch {
        return `NOESIS_PASSKEY_ORIGEM não é uma URL: ${origem}`;
    }
    if (url.protocol !== "https:" && url.hostname !== "localhost") {
        return "NOESIS_PASSKEY_ORIGEM precisa ser https fora de localhost.";
    }
    if (url.hostname !== rpId && !url.hostname.endsWith(`.${rpId}`)) {
        return `NOESIS_PASSKEY_RP_ID (${rpId}) não cobre a origem ${url.hostname}.`;
    }
    return null;
}
//# sourceMappingURL=passkey.js.map